import { useState } from 'react';
import { T, Var, useGT } from 'gt-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { AggregateLineItemsDialog } from './AggregateLineItemsDialog';

interface SelectedLine {
  id: string;
  lineNumber: number;
  description?: string | null;
  totalPrice?: string | null;
}

/** Row checkbox that picks a line for aggregating. */
export function LineSelectCheckbox({
  lineNumber,
  checked,
  onCheckedChange,
}: {
  lineNumber: number;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  const gt = useGT();
  return (
    <Checkbox
      aria-label={gt('Select line {n} to aggregate', {
        n: lineNumber,
        context: 'AP invoice line items: row checkbox for aggregating',
      })}
      checked={checked}
      onCheckedChange={(next) => onCheckedChange(next === true)}
    />
  );
}

/**
 * Action bar shown once two or more lines are checked, with the aggregate
 * dialog it opens.
 */
export function AggregateLinesBar({
  invoiceId,
  supplierId,
  allLineItems,
  selectedLines,
  currency,
  onClear,
}: {
  invoiceId: string;
  supplierId?: string | null;
  allLineItems: { id: string; lineNumber: number }[];
  selectedLines: SelectedLine[];
  currency: string;
  onClear: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      {selectedLines.length >= 2 && (
        <div className="mx-3 flex items-center justify-between rounded-md border bg-muted/40 px-3 py-2 text-sm">
          <span>
            <T context="AP invoice line items: how many lines are selected for aggregating">
              <Var>{selectedLines.length}</Var> lines selected
            </T>
          </span>
          <span className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onClear}>
              <T context="AP invoice line items: clear the aggregate selection">
                Clear
              </T>
            </Button>
            <Button size="sm" onClick={() => setOpen(true)}>
              <T context="AP invoice line items: open the aggregate dialog">
                Aggregate
              </T>
            </Button>
          </span>
        </div>
      )}
      <AggregateLineItemsDialog
        invoiceId={invoiceId}
        supplierId={supplierId}
        allLineItems={allLineItems}
        selectedLineItems={selectedLines}
        currency={currency}
        open={open}
        onOpenChange={setOpen}
        onAggregated={onClear}
      />
    </>
  );
}
